import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from "@angular/core";
import { IContentHeader } from "../../../../../layout/components/content-header/content-header.component";
import { UtilsService } from "../../../../../auth/service/utils.service";
import { InvoiceService } from "../../../invoice.service";
import moment from "moment/moment";
import { ICustomer } from "../../../../customer/customer.interface";
import { IInvoice } from "../../../interfaces/invoice.interface";
import { AuthenticationService } from "../../../../../auth/service";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { CustomerService } from "../../../../customer/customer.service";
import { ListService } from "../../../../../auth/service/list.service";

@Component({
  selector: "app-invoice-archived",
  templateUrl: "./invoice-archived.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InvoiceArchivedComponent implements OnInit, OnDestroy {
  contentHeader: IContentHeader;
  tableLimit: number = 10;

  filterSearch: string = "";
  filterCustomer: number = 0;
  filterYear: number = moment().year();
  filterMonth: number = 0;
  filter_type_order: number = -1;
  filter_detachment: number = 0;

  availableMonths = Array.from({ length: 12 }, (_, i) => i + 1);
  availableYears = Array.from({ length: moment().year() - 2023 + 1 }, (_, i) => 2023 + i);

  private invoices: IInvoice[] = [];
  private _unsubscribeAll: Subject<any> = new Subject();

  customersList: ICustomer[] = [];
  filteredInvoices: IInvoice[] = [];
  sumTotalsAmount: number = 0;
  credit_notes_types_ids = []

  constructor(
    private _utilsService: UtilsService,
    private _invoiceService: InvoiceService,
    private _changeDetectorRef: ChangeDetectorRef,
    private _authenticationService: AuthenticationService,
    private _listService: ListService,
    private _customerService: CustomerService
  ) {
    this._utilsService.disableAutoHideLoading();

    this.contentHeader = {
      headerTitle: "MENU.INVOICES_ARCHIVED",
      actionButton: false,
      breadcrumb: {
        type: "",
        links: [
          {
            name: "Home",
            isLink: true,
            link: "/"
          },
          {
            name: "MENU.INVOICES",
            isLink: false
          },
          {
            name: "MENU.INVOICES_ARCHIVED",
            isLink: false
          }
        ]
      }
    };
  }

  ngOnInit() {
    this._utilsService.showLoading();
    this._customerService.getCustomers().then((result: any) => (this.customersList = result));
    this._listService.getList("invoice_document_type").then((result: any) => {
      this.credit_notes_types_ids = result.filter((result: any) => result.other.credit_note).map((result: any) => result.id)
    });
    this.loadData();
  }

  ngOnDestroy() {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  loadData() {
    this._utilsService.showLoading();
    this._invoiceService.getArchived().pipe(takeUntil(this._unsubscribeAll)).subscribe((data: any) => {
      this.invoices = data;
      if (data.length > 0) {
        this.filter();
        this._changeDetectorRef.detectChanges();
      }
      this._utilsService.hideLoading();
    });
  }

  filter() {
    let filtered: any = this.invoices.map((i: any) => i);

    if (this.filterCustomer > 0) {
      filtered = filtered.filter((i: any) => (i.commission.client.id as number) === this.filterCustomer);
    }

    if (this.filterYear > 0) {
      filtered = filtered.filter((i: any) => i.year === this.filterYear);
    }

    if (this.filterSearch.length > 0) {
      const key: string = this.filterSearch.toLowerCase();
      filtered = filtered.filter((i: any) => i.commission_name.toLowerCase().includes(key));
    }

    this.filterMonth = Number(this.filterMonth);

    if (this.filterMonth > 0) {
      filtered = filtered.filter((i: any) => i.month === this.filterMonth);
    }

    if (this.filter_detachment === 1) {
      filtered = filtered.filter((item: any) => item.detachment_type == "delta");
    } else if (this.filter_detachment === 2) {
      filtered = filtered.filter((item: any) => item.detachment_type == "distacco");
    }

    if (this.filter_type_order > -1) {
      if (this.filter_type_order === 1) {
        filtered = filtered.filter((item: any) => item.commission_type === "task");
      } else if (this.filter_type_order === 0) {
        filtered = filtered.filter((item: any) => item.commission_type === "time_material");
      } else if (this.filter_type_order === 2) {
        filtered = filtered.filter((item: any) => item.commission_type === "product");
      }
    }

    filtered = filtered.map((item: any) => ({ ...item, month_name: moment(`${item.year}-${item.month}-1`, "YYYY-M-D").format("MMMM"), date: `${item.year}-${item.month}-01` }));

    const invoicesAmount = filtered
      .filter((item: any) => !this.credit_notes_types_ids.includes(item.invoice_type.id))
      .map((item: any) => Number(item.total_amount)).reduce((sum: number, total: number) => Number(sum) + Number(total), 0);

    const creditNotesAmount = filtered
      .filter((item: any) => this.credit_notes_types_ids.includes(item.invoice_type.id))
      .map((item: any) => Number(item.total_amount)).reduce((sum: number, total: number) => Number(sum) + Number(total), 0);

    this.sumTotalsAmount = invoicesAmount - creditNotesAmount;

    this.filteredInvoices = filtered;

    this.sumTotalsAmount = filtered.map((item: any) => item.estimate_profit).reduce((sum: number, total: number) => Number(sum) + Number(total), 0);

    this._utilsService.hideLoading();
  }

  async unarchiveInvoice(invoice: any) {
    if (!this._authenticationService.hasCapability("archive_invoice")) {
      window.history.back();
      return false;
    }

    await this._utilsService.translate("ALERT.CONFIRM_UNARCHIVE_INVOICE")
      .then(async (text: string) => {
        await this._utilsService.showSWALDeleteConfirmation({ text: text.replace("COMMISSION_NAME", invoice.commission_name), confirmButtonText: "RESTORE", customClass: { confirmButton: "btn btn-warning" } })
          .then((res: any) => {
            if (res.isConfirmed) {
              let analytics_ids: number[] = (invoice.users || []).map((user: any) => Number(user.analytics_id));
              if (analytics_ids.length < 1) {
                analytics_ids = [invoice.analytics_id];
              }
              this._invoiceService.unarchive({ analytics_ids, commission_type: invoice.commission_type }).pipe(takeUntil(this._unsubscribeAll)).subscribe();
            } else {
              this._utilsService.showSWALError({ title: "ABORTED_TITLE", text: "ABORTED_TEXT" });
            }
          });
      });
  }
}
